/**
 * Route-alias — AUD-CRAWL-K6 (audit d'utilisabilité du 2026-10-09).
 *
 * Une ancienne adresse ou un signet (`/plateforme`, `/tree`, `/graph`, `/sprints`, `/roadmap`,
 * `/backlog`, `/org`, `/sprints/:id`) mène à sa cible en GARDANT le contexte de travail de l'adresse
 * (`?programme=`, `?projet=`, `?perspective=`, cf. `lib/contexteUrl`), que `<Navigate>` perdait.
 *
 * POURQUOI PAS `<Navigate>`
 * -------------------------
 * Le routeur (react-router 7) rend chaque changement d'adresse dans une transition React.
 * `<Navigate>` remplace l'adresse dans son effet, UNE fois : ses dépendances ignorent la recherche.
 * Si, avant le rendu de cette transition, un autre composant renavigue sur l'alias — la barre de
 * contexte qui écrit `?projet=` —, le chemin reste l'alias, l'élément reste monté et son effet ne
 * rejoue pas : page blanche (`/plateforme?projet=…`, 3 chargements sur 6 au crawl). Ici l'effet dépend
 * de la CLÉ de l'adresse : tant que le chemin est l'alias, chaque nouvelle adresse est redirigée.
 */
import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { ecrireContexteUrl, lireContexteUrl } from '@/lib/contexteUrl'

/** Cible d'un alias : une adresse fixe, ou calculée depuis la recherche de l'alias (`/backlog?vue=`). */
export type CibleAlias = string | ((recherche: URLSearchParams) => string)

export function RedirectionAlias({ vers }: { vers: CibleAlias }): null {
  const { key, pathname, search } = useLocation()
  const navigate = useNavigate()
  useEffect(() => {
    const cible = typeof vers === 'function' ? vers(new URLSearchParams(search)) : vers
    const debutRequete = cible.indexOf('?')
    const chemin = debutRequete < 0 ? cible : cible.slice(0, debutRequete)
    const requete = debutRequete < 0 ? '' : cible.slice(debutRequete)
    // Le contexte de l'adresse demandée suit la redirection, dans l'ordre canonique de `ecrireContexteUrl`.
    const suivante = ecrireContexteUrl(requete, lireContexteUrl(search))
    // Garde-fou : un alias qui viserait sa propre adresse redirigerait sans fin.
    if (chemin === pathname && suivante === search) return
    navigate({ pathname: chemin, search: suivante }, { replace: true })
  }, [key, pathname, search, vers, navigate])
  return null
}
